
import Vue from 'vue'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { Component } from 'vue-property-decorator'
import ListComponent from '../../global/components/List/index.vue'
import { centDisplay, prettyDate, toTitleCase, prettyDateTime } from '../../global/functions/util'
import { Job } from '../../global/models'
import { consumeError, viewOnly } from '../../global/utils'

@Component({
  components: { ListComponent }
})
export default class JobList extends Vue {
  get filterOptions() {
    return [
      { text: 'Provider', key: 'providerName', type: 'string' },
      { text: 'Customer', key: 'customerName', type: 'string' },
      { text: 'Language', key: 'language', type: 'string' },
      {
        text: 'Status',
        key: 'status',
        type: 'enum',
        options: [
          { text: 'New', value: 'new' },
          { text: 'In Progress', value: 'in progress' },
          { text: 'Requires Customer Review', value: 'requires customer review' },
          { text: 'Review Completed', value: 'review completed' },
          { text: 'Customer Action Required', value: 'customer action required' },
          { text: 'Completed', value: 'completed' },
          { text: 'Abandoned', value: 'abandoned' }
        ]
      }
    ]
  }

  get availableColumns() {
    return [
      { text: 'Customer', value: 'customerName' },
      { text: 'Provider', value: 'providerName', display: (providerName: string) => providerName || '-' },
      { text: 'Language', value: 'language' },
      { text: 'Submitted', value: 'submitted', display: (date: string) => prettyDate(date) },
      { text: 'Commenced', value: 'commenced', display: (date: string) => prettyDate(date) },
      { text: 'Status', value: 'status', display: (data: string) => toTitleCase(data) },
      { text: 'Total Cost', value: 'total', display: (cost: number) => cost ? centDisplay(cost) : '-' },
      { text: 'Created', value: 'created', type: 'date', display: (date?: string) => prettyDateTime(date) },
      { text: 'Updated', value: 'updated', type: 'date', display: (date?: string) => prettyDateTime(date) }
    ]
  }

  get defaultColumns() {
    return ['language', 'providerName', 'status', 'created', 'updated']
  }

  get actions() {
    return [
      { text: 'View', method: (job: Job) => this.viewJob(job) },
      { text: 'Unassign', method: (job: Job) => this.unassignJob(job), visible: (job: Job) => !!job.providerId && !['completed', 'abandoned'].includes(job.status) && !viewOnly(job) }
    ]
  }

  get list() {
    return this.$refs.list as ListComponent<Job>
  }

  get warn() {
    return [
      {
        icon: 'support_agent',
        color: 'indigo',
        text: 'Customer-broker Job',
        description: 'Customer-broker jobs',
        show: (job: Job) => job.type === 'agency'
      },
      {
        icon: 'support_agent',
        color: 'brown',
        text: 'Customer-initiated Job',
        description: 'Customer-initiated jobs',
        show: (job: Job) => job.type === 'customer'
      }
    ]
  }

  viewJob(job: Job) {
    const jobURL = `${window.location.origin}/jobs/${job.id}`
    if (job.link && jobURL !== job.link) {
      window.location.href = job.link
    } else {
      this.$router.push({ name: 'jobs-read', params: { id: job.id.toString() } })
    }
  }

  async unassignJob(job: Job) {
    try {
      await axios.post(`jobs/${job.id}/unassign`)
      this.list.updateResults()
    } catch (error) {
      const formattedError = consumeError(error)
      if (formattedError.code !== 'SENTRY') {
        this.$store.dispatch('error', formattedError.message)
      } else {
        Sentry.captureException(error)
      }
    }
  }
}
